import { getDepthTopic } from "@/lib/natsConnection";
import { useCallback, useState, useEffect, useMemo } from "react";
import { useGenericSocket } from "./useGenericSocket";
import { environment } from "@/lib/constants";
import {
  useMarginLevelsBySymbol,
  IMarginLevelDetails,
} from "./useMarginLevelsBySymbol";
import { useBinanceSymbols } from "./useBinanceSymbols";
import axios from "axios";

export interface IDepthOfMarketUpdateResponse {
  e: string;
  E: number;
  T: number;
  s: string;
  U: number;
  u: number;
  pu: number;
  b: [string, string][];
  a: [string, string][];
}

export interface IDepthOfMarketSnapshotResponse {
  lastUpdateId: number;
  timestamp: number;
  tradeTime: number;
  bids: [number, number][];
  asks: [number, number][];
}

export interface IOrderBook {
  bids: Map<
    number,
    {
      size: number;
      usdValue: number;
      marginLevel: number;
      marginLevelSide: string;
    }
  >;
  asks: Map<
    number,
    {
      size: number;
      usdValue: number;
      marginLevel: number;
      marginLevelSide: string;
    }
  >;
  maxBidVolume: number;
  maxAskVolume: number;
  sumBidVolume: number;
  sumAskVolume: number;
  maxBidUsdVolume: number;
  maxAskUsdVolume: number;
  sumBidUsdVolume: number;
  sumAskUsdVolume: number;
  tickSize: number;
  tickDecimals: number;
}

export function useDepthOfMarket(symbol: string, scale: number = 1) {
  const { data: marginLevels } = useMarginLevelsBySymbol(symbol);
  const { data: symbols, isLoading: isSymbolsLoading } = useBinanceSymbols();
  const symbolInfo = useMemo(() => {
    const info = symbols?.find((s) => s.symbol === symbol);
    return info;
  }, [symbols, symbol]);
  const priceFilter = useMemo(() => {
    const filter = symbolInfo?.filters?.find(
      (f) => f.filterType === "PRICE_FILTER",
    );
    return filter;
  }, [symbolInfo]);

  const tickSize = useMemo(() => {
    if (!priceFilter) {
      return 0.01;
    }
    return Number(priceFilter.tickSize);
  }, [priceFilter]);

  const tickDecimals = useMemo(() => {
    if (tickSize < 1e-6) {
      return priceFilter?.tickSize.split(".")[1]?.length ?? 0;
    }
    return tickSize.toString().split(".")[1]?.length ?? 0;
  }, [tickSize, priceFilter]);

  const parsePrice = useCallback(
    (price: string | number) => {
      if (tickSize < 1e-6) {
        return typeof price === "string" ? Number(price) : price;
      }
      return Number(price);
    },
    [tickSize],
  );

  const initData = useCallback(async (): Promise<IOrderBook> => {
    if (isSymbolsLoading || !symbolInfo) {
      throw new Error("Cannot initialize data - dependencies not ready");
    }

    const snapshotUrl = `${environment.binanceFuturesDepthURL}?symbol=${symbol.toUpperCase()}&limit=1&timeFrame=1m`;
    const snapshotData =
      await axios.get<IDepthOfMarketSnapshotResponse[]>(snapshotUrl);

    return {
      bids: new Map(
        snapshotData.data[0].bids.slice(0, 100).map(([price, size]) => [
          parsePrice(price),
          {
            size: Number(size),
            usdValue: 0,
            marginLevel: 0,
            marginLevelSide: "",
          },
        ]),
      ),
      asks: new Map(
        snapshotData.data[0].asks.slice(0, 100).map(([price, size]) => [
          parsePrice(price),
          {
            size: Number(size),
            usdValue: 0,
            marginLevel: 0,
            marginLevelSide: "",
          },
        ]),
      ),
      maxBidVolume: 0,
      maxAskVolume: 0,
      sumBidVolume: 0,
      sumAskVolume: 0,
      maxBidUsdVolume: 0,
      maxAskUsdVolume: 0,
      sumBidUsdVolume: 0,
      sumAskUsdVolume: 0,
      tickSize,
      tickDecimals,
    };
  }, [
    symbol,
    symbolInfo,
    tickDecimals,
    tickSize,
    isSymbolsLoading,
    parsePrice,
  ]);

  // Helper function to group prices by scale
  const groupByScale = useCallback(
    (
      entries: [
        number,
        {
          size: number;
          usdValue: number;
          marginLevel: number;
          marginLevelSide: string;
        },
      ][],
      isAsk: boolean,
    ) => {
      const grouped = new Map<
        number,
        {
          size: number;
          usdValue: number;
          marginLevel: number;
          marginLevelSide: string;
        }
      >();

      // If there are no entries, return empty map
      if (entries.length === 0) {
        return grouped;
      }

      // First pass: group existing entries by rounded price
      entries.forEach(([price, values]) => {
        const scaledStep = scale * tickSize;
        const scaledPrice = isAsk
          ? Math.ceil(price / scaledStep) * scaledStep
          : Math.floor(price / scaledStep) * scaledStep;
        const roundedPrice = Number(scaledPrice.toFixed(tickDecimals));

        const existing = grouped.get(roundedPrice);

        if (existing) {
          grouped.set(roundedPrice, {
            size: existing.size + values.size,
            usdValue: existing.usdValue + values.usdValue,
            marginLevel: Math.max(existing.marginLevel, values.marginLevel),
            marginLevelSide: values.marginLevelSide || existing.marginLevelSide,
          });
        } else {
          grouped.set(roundedPrice, { ...values });
        }
      });

      // Second pass: add a limited number of empty price levels to ensure consistent steps
      // This is a more efficient approach than filling in all possible price levels
      const prices = Array.from(grouped.keys()).sort((a, b) => a - b);
      const minPrice = prices[0];
      const maxPrice = prices[prices.length - 1];
      const scaledStep = scale * tickSize;

      // Limit the number of price levels to add to prevent performance issues
      // Only add up to 100 empty price levels
      const maxEmptyLevelsToAdd = 100;
      let emptyLevelsAdded = 0;

      // Find gaps in price levels and fill them with empty levels
      for (let i = 0; i < prices.length - 1; i++) {
        const currentPrice = prices[i];
        const nextPrice = prices[i + 1];

        // Calculate how many steps should be between these prices
        const expectedSteps = Math.round(
          (nextPrice - currentPrice) / scaledStep,
        );

        // If there's a gap (more than 1 step), add empty price levels
        if (expectedSteps > 1 && emptyLevelsAdded < maxEmptyLevelsToAdd) {
          // Add up to 5 empty levels per gap to avoid adding too many at once
          const stepsToAdd = Math.min(
            expectedSteps - 1,
            5,
            maxEmptyLevelsToAdd - emptyLevelsAdded,
          );

          for (let step = 1; step <= stepsToAdd; step++) {
            const emptyPrice = Number(
              (currentPrice + step * scaledStep).toFixed(tickDecimals),
            );

            if (!grouped.has(emptyPrice)) {
              grouped.set(emptyPrice, {
                size: 0,
                usdValue: 0,
                marginLevel: 0,
                marginLevelSide: "",
              });
              emptyLevelsAdded++;
            }
          }
        }
      }

      return grouped;
    },
    [scale, tickSize, tickDecimals],
  );

  const handler = useCallback(
    (
      _: IOrderBook | undefined,
      newData: IDepthOfMarketUpdateResponse,
    ): IOrderBook => {
      // Skip processing if no data
      if (!newData?.b || !newData?.a) {
        return (
          _ || {
            bids: new Map(),
            asks: new Map(),
            maxBidVolume: 0,
            maxAskVolume: 0,
            sumBidVolume: 0,
            sumAskVolume: 0,
            maxBidUsdVolume: 0,
            maxAskUsdVolume: 0,
            sumBidUsdVolume: 0,
            sumAskUsdVolume: 0,
            tickSize,
            tickDecimals,
          }
        );
      }

      // Return existing data if it hasn't changed
      if (_ && newData.b.length === 0 && newData.a.length === 0) {
        return _;
      }

      // Create new maps only if we don't have existing ones
      const rawBids =
        _?.bids ||
        new Map<
          number,
          {
            size: number;
            usdValue: number;
            marginLevel: number;
            marginLevelSide: string;
          }
        >();
      const rawAsks =
        _?.asks ||
        new Map<
          number,
          {
            size: number;
            usdValue: number;
            marginLevel: number;
            marginLevelSide: string;
          }
        >();

      // Store processed margin levels to avoid redundant processing
      let processedMarginLevels: IMarginLevelDetails[] | null = null;

      // Process updates in batches
      const processBatch = (
        entries: [string, string][],
        map: Map<
          number,
          {
            size: number;
            usdValue: number;
            marginLevel: number;
            marginLevelSide: string;
          }
        >,
        isAsk: boolean,
      ) => {
        for (let i = 0; i < entries.length; i++) {
          const [price, size] = entries[i];
          const numPrice = parsePrice(price);
          const numSize = Number(size);
          const scaledStep = scale * tickSize;
          const scaledPrice = isAsk
            ? Math.ceil(numPrice / scaledStep) * scaledStep
            : Math.floor(numPrice / scaledStep) * scaledStep;

          // Format the price with the correct number of decimal places
          // This ensures consistent display (e.g., 0.13 will be displayed as 0.1300)
          const formattedPrice = scaledPrice.toFixed(tickDecimals);
          const roundedPrice = Number(formattedPrice);

          // Debug the price rounding
          if (i === 0) {
            console.log(
              `Original price: ${numPrice}, Scaled: ${scaledPrice}, Formatted: ${formattedPrice}, Rounded: ${roundedPrice}`,
            );
          }

          if (numSize === 0) {
            // If size is 0, remove the price level
            map.delete(roundedPrice);
          } else {
            // Find margin level within the scaled price range
            let matchedMarginLevel = null;

            // Check if we have margin levels
            if (marginLevels && marginLevels.length > 0) {
              // First, log some information about the margin levels
              if (isAsk && i === 0) {
                // Only log once per batch for asks
                console.log(
                  `Processing margin levels: ${marginLevels.length} levels available`,
                );
                console.log(
                  `First few margin levels:`,
                  marginLevels.slice(0, 3),
                );
                console.log(
                  `Price range in margin levels: ${Math.min(...marginLevels.map((ml) => ml.price))} - ${Math.max(...marginLevels.map((ml) => ml.price))}`,
                );
                console.log(
                  `Current price range in DOM: ${numPrice}, rounded: ${roundedPrice}`,
                );
                console.log(
                  `Tick size: ${tickSize}, Scaled step: ${scaledStep}`,
                );
              }

              // Log all margin levels and their rounded prices for debugging
              if (isAsk && i === 0) {
                console.log("All margin levels with rounded prices:");
                marginLevels.forEach((ml) => {
                  const mlRoundedPrice = Number(ml.price.toFixed(tickDecimals));
                  console.log(
                    `ML price: ${ml.price}, rounded: ${mlRoundedPrice}, quantity: ${ml.quoteQuantity}`,
                  );
                });
              }

              // Process margin levels to match with DOM price levels
              // We want to find margin levels that match the current price point
              // without affecting the DOM prices and volumes

              // First, prepare a map of rounded prices to margin levels if not already done
              if (i === 0 && isAsk) {
                // Only do this once per batch to avoid redundant processing
                if (!processedMarginLevels) {
                  // Create a map to store rounded margin levels
                  const roundedMarginLevelsMap = new Map<
                    number,
                    IMarginLevelDetails
                  >();

                  console.log(
                    `Processing ${marginLevels.length} margin levels with scale ${scale} and tickSize ${tickSize}`,
                  );

                  // Process each margin level
                  for (const ml of marginLevels) {
                    // Apply the same rounding logic as for prices in the DOM
                    // This ensures margin levels will match DOM price points
                    const mlScaledStep = scale * tickSize;
                    const mlScaledPrice =
                      ml.side === "DOWN" // DOWN is for ask side
                        ? Math.ceil(ml.price / mlScaledStep) * mlScaledStep
                        : Math.floor(ml.price / mlScaledStep) * mlScaledStep;

                    // Round to the same number of decimal places
                    const mlRoundedPrice = Number(
                      mlScaledPrice.toFixed(tickDecimals),
                    );

                    console.log(
                      `Rounding ML: original=${ml.price}, scaled=${mlScaledPrice}, rounded=${mlRoundedPrice}`,
                    );

                    // If we already have a margin level at this rounded price, sum the quantities
                    if (roundedMarginLevelsMap.has(mlRoundedPrice)) {
                      const existingML =
                        roundedMarginLevelsMap.get(mlRoundedPrice)!;
                      existingML.quoteQuantity += ml.quoteQuantity;
                      roundedMarginLevelsMap.set(mlRoundedPrice, existingML);
                      console.log(
                        `  Summing quantities at ${mlRoundedPrice}: new total=${existingML.quoteQuantity}`,
                      );
                    } else {
                      // Otherwise, add this margin level to the map
                      roundedMarginLevelsMap.set(mlRoundedPrice, {
                        ...ml,
                        price: mlRoundedPrice,
                      });
                      console.log(
                        `  Adding new margin level at ${mlRoundedPrice}: quantity=${ml.quoteQuantity}`,
                      );
                    }
                  }

                  // Convert the map to an array for easier use
                  processedMarginLevels = Array.from(
                    roundedMarginLevelsMap.values(),
                  );
                  console.log(
                    `Created ${processedMarginLevels.length} processed margin levels:`,
                    processedMarginLevels,
                  );
                }
              }

              // Now find the exact match in our processed margin levels
              // We only look for exact price matches to ensure we're not affecting DOM prices
              if (processedMarginLevels) {
                // Find margin level that exactly matches this DOM price point
                matchedMarginLevel = processedMarginLevels.find(
                  (ml) => ml.price === roundedPrice,
                );

                if (matchedMarginLevel && isAsk && i === 0) {
                  console.log(
                    `Found exact match for DOM price ${roundedPrice}: ML price=${matchedMarginLevel.price}, quantity=${matchedMarginLevel.quoteQuantity}`,
                  );
                }
              }
            } else {
              // Log that we don't have margin levels
              if (isAsk && i === 0) {
                // Only log once per batch
                console.log(`No margin levels available for ${symbol}`);
              }
            }

            // Log if we found a match
            if (matchedMarginLevel && isAsk && i === 0) {
              // Only log once per batch for asks
              console.log(
                `Found match for ${roundedPrice}: quantity=${matchedMarginLevel.quoteQuantity}, side=${matchedMarginLevel.side}`,
              );
            }

            // Update the price level with new size
            // The DOM is the base - we only add margin level info without affecting DOM prices/volumes
            map.set(roundedPrice, {
              size: numSize,
              usdValue: numSize * numPrice,
              marginLevel: matchedMarginLevel?.quoteQuantity || 0,
              marginLevelSide: matchedMarginLevel?.side || "",
            });
          }
        }
      };

      // Process updates
      processBatch(newData.b, rawBids, false);
      processBatch(newData.a, rawAsks, true);

      // Instead of filling in all missing price levels (which caused performance issues),
      // we'll modify the groupByScale function to ensure consistent price steps

      // Group by scale
      const bids = groupByScale(Array.from(rawBids.entries()), false);
      const asks = groupByScale(Array.from(rawAsks.entries()), true);

      // Get current price from the order book
      const currentPrice = (() => {
        const lowestAsk = Math.min(...Array.from(asks.keys()));
        const highestBid = Math.max(...Array.from(bids.keys()));
        return (lowestAsk + highestBid) / 2;
      })();

      // Filter out invalid prices
      const validBids = new Map(
        Array.from(bids.entries()).filter(([price]) => price <= currentPrice),
      );
      const validAsks = new Map(
        Array.from(asks.entries()).filter(([price]) => price >= currentPrice),
      );

      // Calculate max and sum volumes
      let maxBidVolume = 0;
      let maxAskVolume = 0;
      let sumBidVolume = 0;
      let sumAskVolume = 0;
      let maxBidUsdVolume = 0;
      let maxAskUsdVolume = 0;
      let sumBidUsdVolume = 0;
      let sumAskUsdVolume = 0;

      // Calculate bid volumes
      for (const { size, usdValue } of validBids.values()) {
        maxBidVolume = Math.max(maxBidVolume, size);
        sumBidVolume += size;
        maxBidUsdVolume = Math.max(maxBidUsdVolume, usdValue);
        sumBidUsdVolume += usdValue;
      }

      // Calculate ask volumes
      for (const { size, usdValue } of validAsks.values()) {
        maxAskVolume = Math.max(maxAskVolume, size);
        sumAskVolume += size;
        maxAskUsdVolume = Math.max(maxAskUsdVolume, usdValue);
        sumAskUsdVolume += usdValue;
      }

      return {
        bids: validBids,
        asks: validAsks,
        maxBidVolume,
        maxAskVolume,
        sumBidVolume,
        sumAskVolume,
        maxBidUsdVolume,
        maxAskUsdVolume,
        sumBidUsdVolume,
        sumAskUsdVolume,
        tickSize,
        tickDecimals,
      };
    },
    [
      groupByScale,
      tickSize,
      tickDecimals,
      scale,
      marginLevels,
      parsePrice,
      symbol,
    ],
  );

  return useGenericSocket<IOrderBook, IDepthOfMarketUpdateResponse>(
    getDepthTopic(symbol),
    handler,
    {
      fetchInitialData: initData,
    },
  );
}
